import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import type { Loan } from '@/types'
import { loansService } from '@/services/loans'
import { ingestionService } from '@/services/ingestion'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import {
  borrowingEntity,
  criticalMissing,
  derivedBasis,
  loanExceptions,
  money,
  moneyRecorded,
  pct,
} from './helpers'
import { ltvWithImplied } from '@/components/deal/dossier/valueBasis'
import {
  customValue,
  debtYieldPct,
  debtYieldText,
  loanFees,
  loanGrossRevenue,
  loanNoi,
  loanPropertyValue,
  loanSpread,
} from './loanFigures'
import { loanCoverageRows } from './loanCoverage'
import { StatedFiguresBlock } from '@/components/deal/dossier/StatedFiguresBlock'
import type { StatedFigure } from './types'

function SectionLabel({ label, action }: { label: string; action?: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-stone-500">{label}</div>
      {action ? <div className="text-[10px] text-amber-200/80">{action}</div> : null}
    </div>
  )
}

function MetricCell({ label, value, basis }: { label: string; value: string; basis?: string | null }) {
  return (
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">
        {label}
        {/* A figure the platform computed says so, with its formula: The
            Ardsley's hotel DSCR read "1.53x" here with no basis (nd3-10). */}
        {basis ? (
          <span title={basis} className="ml-1 rounded bg-violet-400/10 px-1 text-[8px] text-violet-300">
            Derived
          </span>
        ) : null}
      </div>
      <div className="mt-0.5 truncate text-[13px] font-medium text-stone-100" title={basis ?? undefined}>
        {value}
      </div>
      {basis ? <div className="truncate text-[9px] text-violet-300/70">{basis}</div> : null}
    </div>
  )
}

function customNumber(loan: Loan, keys: string[]): number | null {
  for (const key of keys) {
    // Custom field keys arrive camel-cased; read them under either spelling.
    const raw = customValue(loan, key)
    if (typeof raw === 'number' && !Number.isNaN(raw)) return raw
    if (typeof raw === 'string' && raw.trim() !== '') {
      const n = Number(raw)
      if (!Number.isNaN(n)) return n
    }
  }
  return null
}

function formatActivityWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const when = new Date(iso)
  if (Number.isNaN(when.getTime())) return iso.slice(0, 16)
  const now = new Date()
  const sameDay = when.toDateString() === now.toDateString()
  if (sameDay) {
    return when.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  }
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (when.toDateString() === yesterday.toDateString()) return 'Yesterday'
  return when.toLocaleDateString([], { month: 'short', day: 'numeric' })
}

export function EconomicsTab({
  loan,
  statedFigures,
}: {
  loan: Loan
  /** The broker-stated figures beside the platform's own, from the field lineage. */
  statedFigures?: readonly StatedFigure[]
}) {
  // Value, debt yield, spread, fees, revenue and NOI live in the row's
  // catalogue custom fields; the top-level keys this read were never sent.
  const revenue = loanGrossRevenue(loan)
  const noi = loanNoi(loan)
  const roe = customNumber(loan, ['risk_adjusted_roe', 'roe', 'risk_adj_roe'])

  const left = [
    {
      label: 'Value',
      value: money(loanPropertyValue(loan)),
      basis: loanPropertyValue(loan) != null ? derivedBasis(loan, 'property_value') : null,
    },
    { label: 'Loan', value: moneyRecorded(loan.loanAmount) },
    {
      // The stated LTV, and beside it the one the deal's own loan and value
      // imply when they differ — the dossier header's rule and wording. A
      // bare 58.0% stood next to a value implying 69% (other-13).
      label: 'LTV',
      value: ltvWithImplied(loan.ltv, loan.loanAmount, loanPropertyValue(loan)),
      basis: loan.ltv > 0 ? derivedBasis(loan, 'ltv') : null,
    },
    {
      label: 'Debt Yield',
      value: debtYieldText(loan, debtYieldPct),
      basis: derivedBasis(loan, 'debt_yield'),
    },
    {
      label: 'DSCR',
      value: loan.dscr > 0 ? `${loan.dscr.toFixed(2)}x` : '—',
      basis: loan.dscr > 0 ? derivedBasis(loan, 'dscr') : null,
    },
  ]
  const right = [
    { label: 'Spread', value: loanSpread(loan) ?? '—' },
    { label: 'Fees', value: loanFees(loan) ?? '—' },
    { label: 'Revenue', value: money(revenue) },
    {
      label: 'Risk-adjusted ROE',
      value: roe != null ? pct(roe) : '—',
    },
  ]

  return (
    <div className="space-y-4">
      <section>
        <SectionLabel label="Economics" action={loan.stage} />
        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-white/[0.04] pt-3">
          <div className="space-y-2.5 border-r border-white/[0.06] pr-3">
            {left.map((item) => (
              <MetricCell key={item.label} label={item.label} value={item.value} basis={item.basis} />
            ))}
          </div>
          <div className="space-y-2.5 pl-1">
            {right.map((item) => (
              <MetricCell key={item.label} label={item.label} value={item.value} />
            ))}
          </div>
        </div>
        <p className="mt-3 text-[10px] text-stone-600">
          Blank metrics are not on this deal record yet — nothing is invented here.
        </p>
      </section>

      {/* Coverage on the lender's own basis, kept apart from what the broker
          stated: interest-only, Actual/360 unless the deal says otherwise, and
          — a hotel — after the entered FF&E reserve. Each row states its basis,
          and what a blank one is waiting on (WP-G). */}
      <section data-testid="inspector-coverage">
        <SectionLabel label="Debt service coverage" />
        <dl className="mt-2 space-y-2 border-t border-white/[0.04] pt-3">
          {loanCoverageRows(loan).map((row) => (
            <div key={row.id} data-coverage={row.id}>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{row.label}</dt>
                <dd className="text-[13px] font-medium text-stone-100">
                  {row.value != null ? `${row.value.toFixed(2)}x` : '—'}
                </dd>
              </div>
              <p className="text-[9px] leading-snug text-stone-600" title={row.basis}>{row.basis}</p>
            </div>
          ))}
        </dl>
      </section>

      {/* The cap rate, value, NOI and debt yield a broker stated, kept as stated,
          with the platform's own figure and the variance beside each (Gate 0 D1). */}
      <StatedFiguresBlock figures={statedFigures} />

      {(noi != null || loan.rate) && (
        <section>
          <SectionLabel label="Related" />
          <div className="mt-2 grid grid-cols-2 gap-2 text-[12px] text-stone-300">
            <div>
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">NOI</span>
              <div>{money(noi)}</div>
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">Rate</span>
              <div>{loan.rate || '—'}</div>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}

export function BlockersTab({ loan }: { loan: Loan }) {
  const missing = criticalMissing(loan)
  const exceptions = loanExceptions(loan).filter((item) => item !== 'Critical data missing')
  const openTriggers = (loan.triggers ?? []).filter((t) => t.status !== 'resolved')
  const overdue = openTriggers.filter((t) => t.status === 'overdue')
  // The documents a deal holds are its vault uploads AND the files it arrived
  // with (the email and its attachments) — what the dossier's Documents tab
  // counts. `loan.documents` alone is empty on every email-only deal, which
  // put a false "no documents" blocker on each of them.
  const sourcePkg = useQuery({
    queryKey: ['ingestion-source', loan.sourceIngestionId],
    queryFn: () => ingestionService.getById(loan.sourceIngestionId!),
    enabled: Boolean(loan.sourceIngestionId),
    staleTime: 60_000,
  })
  const sourceFileCount = sourcePkg.data?.rawFilePaths?.length ?? 0
  const documentsKnown = !loan.sourceIngestionId || sourcePkg.isSuccess || sourcePkg.isError

  const blockers: string[] = []
  if (missing.length > 0) blockers.push(`${missing.length} critical field${missing.length === 1 ? '' : 's'} missing`)
  if (
    documentsKnown &&
    (loan.documents ?? []).length + sourceFileCount === 0 &&
    loan.stage !== 'Funded'
  ) {
    blockers.push('No documents linked on this record')
  }
  for (const trigger of overdue) {
    blockers.push(trigger.description || trigger.type || 'Overdue requirement')
  }
  for (const item of exceptions) {
    if (!blockers.includes(item)) blockers.push(item)
  }
  for (const trigger of openTriggers.filter((t) => t.status !== 'overdue')) {
    const label = trigger.description || trigger.type
    if (label && !blockers.includes(label)) blockers.push(label)
  }

  const parties = [
    // A required party: when the package names none, the slot says so rather
    // than vanishing (Cranbury listed only its borrowing entity and broker).
    { role: 'Sponsor', name: loan.sponsor?.trim() || 'Not stated' },
    // The entity that signs the note, never the sponsor's name copied over
    // (PKG-09: "BORROWER Quarry Hill Partners | SPONSOR Quarry Hill Partners").
    { role: 'Borrowing entity', name: borrowingEntity(loan) || 'Not stated' },
    { role: 'Broker', name: loan.brokerName?.trim() || '' },
    { role: 'Assignee', name: loan.assignee?.trim() || '' },
  ].filter((p) => p.name)

  return (
    <div className="space-y-5">
      <section>
        <SectionLabel
          label="Blockers"
          action={blockers.length ? `${blockers.length} open` : 'Clear'}
        />
        <div className="mt-2 flex flex-wrap gap-1.5">
          {blockers.length === 0 ? (
            <div className="rounded-md border border-emerald-400/15 bg-emerald-400/[0.05] px-3 py-2 text-[12px] text-emerald-200">
              No open blockers on this deal.
            </div>
          ) : (
            blockers.map((item) => (
              <span
                key={item}
                className="rounded border border-red-400/35 px-2 py-1 text-[11px] text-red-200"
              >
                {item}
              </span>
            ))
          )}
        </div>
        {missing.length > 0 ? (
          <ul className="mt-2 space-y-1 text-[11px] text-stone-400">
            {missing.map((field) => (
              <li key={field}>Missing field: {field}</li>
            ))}
          </ul>
        ) : null}
        {(loan.creditBoxExceptions ?? []).length > 0 && loan.creditBoxRulesNote ? (
          <div className="mt-2 text-[11px] text-stone-500">
            Credit box thresholds: {loan.creditBoxRulesNote}
          </div>
        ) : null}
      </section>

      <section>
        <SectionLabel label="Parties" action={parties.length ? `${parties.length}` : undefined} />
        <div className="mt-2 space-y-1.5">
          {parties.length === 0 ? (
            <div className="rounded-md border border-stone-700/60 px-3 py-3 text-[12px] text-stone-500">
              No parties recorded on this deal yet.
            </div>
          ) : (
            parties.map((party) => (
              <div
                key={party.role}
                className="flex items-center justify-between gap-2 rounded-md border border-white/[0.04] px-2.5 py-2"
              >
                <div className="min-w-0">
                  <div className="text-[9px] uppercase tracking-[0.14em] text-stone-600">{party.role}</div>
                  <div className="truncate text-[12px] text-stone-200">{party.name}</div>
                </div>
                <ExternalLink className="h-3 w-3 shrink-0 text-stone-600" aria-hidden />
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  )
}

export function ActivitySummaryTab({
  loan,
  onViewAll,
}: {
  loan: Loan
  onViewAll: () => void
}) {
  const notesQuery = useQuery({
    queryKey: ['loan-notes', loan.id],
    queryFn: () => loansService.listNotes(loan.id),
  })

  const activityQuery = useQuery({
    queryKey: LOAN_QUERY_KEYS.activity(loan.id),
    queryFn: () => loansService.getActivity(loan.id),
  })

  const items = useMemo(() => {
    const notes = (notesQuery.data ?? []).map((n) => ({
      id: `note-${n.id}`,
      at: n.createdAt,
      text: `${n.author || 'Someone'} · ${n.body.split('\n')[0] || 'Note'}`,
    }))
    const events = (activityQuery.data ?? []).map((e) => ({
      id: `act-${e.id}`,
      at: e.createdAt,
      text: [e.userEmail, e.eventType].filter(Boolean).join(' · ') || e.eventType || 'Event',
    }))
    return [...notes, ...events]
      .sort((a, b) => {
        const ta = a.at ? new Date(a.at).getTime() : 0
        const tb = b.at ? new Date(b.at).getTime() : 0
        return tb - ta
      })
      .slice(0, 12)
  }, [notesQuery.data, activityQuery.data])

  const loading = notesQuery.isLoading || activityQuery.isLoading
  const failed = notesQuery.isError && activityQuery.isError

  return (
    <div className="space-y-3">
      <SectionLabel
        label="Activity"
        action={
          loading
            ? 'Loading…'
            : failed
              ? 'Unavailable'
              : items.length
                ? `${items.length} recent`
                : undefined
        }
      />
      {loading ? (
        <div className="py-6 text-center text-sm text-stone-500">Loading activity…</div>
      ) : failed ? (
        <div className="rounded-md border border-red-400/20 px-3 py-3 text-[12px] text-red-300">
          Unable to load activity for this deal.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border border-stone-700/60 px-3 py-4 text-[12px] text-stone-400">
          No notes or system events recorded yet.
        </div>
      ) : (
        <ol className="relative space-y-0 border-l border-white/[0.08] pl-3">
          {items.map((item) => (
            <li key={item.id} className="relative pb-3 last:pb-0">
              <span className="absolute -left-[17px] top-1.5 h-2 w-2 rounded-full border border-amber-300/50 bg-[#0a0a0a]" />
              <div className="text-[10px] text-stone-500">{formatActivityWhen(item.at)}</div>
              <div className="mt-0.5 text-[12px] text-stone-200">{item.text}</div>
            </li>
          ))}
        </ol>
      )}
      <button
        type="button"
        onClick={onViewAll}
        className="text-[10px] uppercase tracking-[0.14em] text-amber-200/80 hover:text-amber-100"
      >
        View all activity →
      </button>
    </div>
  )
}
